import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme/ThemeContext';
import apiClient from '../../../components/api/apiClient';
import { WorkflowLogModalToolbar, WORKFLOW_LOG_AUTO_REFRESH_MS } from './WorkflowLogModalToolbar';

export interface CategoryDetailContext {
  workflowId: string;
  runId: string;
  nodeId: string;
  categoryType?: string | null;
  categoryId?: string | null;
}

interface Props {
  nodeName: string;
  nodeTime: string;
  onClose: () => void;
  status: 'Success' | 'Failed' | 'Running';
  categoryDetail: CategoryDetailContext;
}

type DetailBlock = {
  kind?: string;
  title?: string | null;
  text?: string | null;
  code?: string | null;
  codeLanguage?: string | null;
  table?: unknown;
};

type CategoryDetailResponse = {
  workflowId?: string;
  runId?: string;
  nodeId?: string;
  categoryType?: string;
  categoryId?: string;
  blocks?: DetailBlock[];
};

function resolveRunLogsBaseUrl(): string {
  const raw = (process.env.REACT_APP_API_URL_workflow_run_logs ?? '').trim();
  return raw.replace(/\/+$/, '');
}

function categoryDetailErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: unknown; status?: number } }).response;
    const d = resp?.data;
    if (typeof d === 'string' && d.trim()) {
      try {
        const o = JSON.parse(d) as Record<string, unknown>;
        const m = o.message ?? o.title ?? o.detail;
        if (typeof m === 'string' && m.trim()) return m.trim();
      } catch {
        return d.trim();
      }
    }
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      const m = o.message ?? o.title ?? o.detail ?? o.error;
      if (typeof m === 'string' && m.trim()) return m.trim();
    }
    if (resp?.status != null) return `Could not load detail (HTTP ${resp.status}).`;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Could not load detail.';
}

function parseCategoryDetailPayload(data: unknown): CategoryDetailResponse {
  if (typeof data === 'string') {
    const t = data.trim();
    if (!t) return {};
    return JSON.parse(t) as CategoryDetailResponse;
  }
  if (data && typeof data === 'object') {
    return data as CategoryDetailResponse;
  }
  return {};
}

function normalizeCategoryTypeForQuery(raw: string | null | undefined): string {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'llms') return 'llm';
  return (raw ?? '').trim();
}

function prettyFormatCode(code: string | null | undefined, codeLanguage: string | null | undefined): string {
  const raw = code ?? '';
  const lang = (codeLanguage ?? '').toLowerCase();
  if (lang === 'json' || lang === '' || lang === 'javascript' || lang === 'js') {
    try {
      const trimmed = raw.trim();
      if (!trimmed) return '';
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return raw;
    }
  }
  return raw;
}

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const o = err as { code?: string; name?: string };
  return o.code === 'ERR_CANCELED' || o.name === 'CanceledError' || o.name === 'AbortError';
}

async function fetchCategoryDetail(
  ctx: CategoryDetailContext,
  signal?: AbortSignal
): Promise<CategoryDetailResponse> {
  const base = resolveRunLogsBaseUrl();
  if (!base) {
    throw new Error(
      'Missing REACT_APP_API_URL_workflow_run_logs. Add it to your environment (see .env.example), then restart the dev server.'
    );
  }
  const wid = encodeURIComponent(ctx.workflowId.trim());
  const rid = encodeURIComponent(ctx.runId.trim());
  const path = `/workflows/${wid}/run-logs/${rid}/category-detail`;

  const { data } = await apiClient.get<string>(path, {
    baseURL: base,
    params: {
      nodeId: ctx.nodeId.trim(),
      categoryType: normalizeCategoryTypeForQuery(ctx.categoryType),
      categoryId: (ctx.categoryId ?? '').trim(),
    },
    headers: { Accept: 'text/plain, application/json' },
    responseType: 'text',
    signal,
  });

  return parseCategoryDetailPayload(data);
}

function renderTable(table: unknown, isLight: boolean): React.ReactNode {
  if (!Array.isArray(table) || table.length === 0) return null;
  const rows = table as unknown[][];
  const wrap = isLight
    ? 'border-slate-200 bg-slate-50'
    : 'border-slate-700/80 bg-slate-950/50';
  const cellBorder = isLight ? 'border-slate-200' : 'border-slate-800';
  const text = isLight ? 'text-slate-800' : 'text-slate-200';
  return (
    <div className={`overflow-x-auto rounded-lg border ${wrap}`}>
      <table className={`w-full border-collapse text-xs ${text}`}>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={`border-b ${cellBorder} last:border-b-0`}>
              {(Array.isArray(row) ? row : []).map((cell, j) => (
                <td key={j} className={`border-r ${cellBorder} px-3 py-2 align-top last:border-r-0`}>
                  {cell == null ? '' : typeof cell === 'object' ? JSON.stringify(cell, null, 2) : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DetailedNodeLogModal: React.FC<Props> = ({
  nodeName,
  nodeTime,
  onClose,
  status,
  categoryDetail,
}) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const statusBadge =
    status === 'Success'
      ? isLight
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : 'border-emerald-800/60 bg-emerald-950/30 text-emerald-300'
      : status === 'Failed'
        ? isLight
          ? 'border-rose-200 bg-rose-50 text-rose-800'
          : 'border-rose-800/60 bg-rose-950/30 text-rose-300'
        : isLight
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-amber-800/60 bg-amber-950/30 text-amber-300';

  const [payload, setPayload] = useState<CategoryDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [bgRefreshing, setBgRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const categoryDetailRef = useRef(categoryDetail);
  categoryDetailRef.current = categoryDetail;

  const categoryDetailKey = useMemo(
    () =>
      `${categoryDetail.workflowId.trim()}|${categoryDetail.runId.trim()}|${categoryDetail.nodeId.trim()}|${(categoryDetail.categoryType ?? '').trim()}|${(categoryDetail.categoryId ?? '').trim()}`,
    [
      categoryDetail.workflowId,
      categoryDetail.runId,
      categoryDetail.nodeId,
      categoryDetail.categoryType,
      categoryDetail.categoryId,
    ]
  );

  const runFetch = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) {
      setLoading(true);
      setPayload(null);
    } else {
      setBgRefreshing(true);
    }
    setError(null);
    try {
      const res = await fetchCategoryDetail(categoryDetailRef.current);
      setPayload(res);
    } catch (e) {
      if (!isAbortError(e)) {
        setPayload(null);
        setError(categoryDetailErrorMessage(e));
      }
    } finally {
      if (!silent) setLoading(false);
      else setBgRefreshing(false);
    }
  }, []);

  const runFetchRef = useRef(runFetch);
  runFetchRef.current = runFetch;

  useEffect(() => {
    void runFetchRef.current({ silent: false });
  }, [categoryDetailKey]);

  const autoRefreshRef = useRef(autoRefresh);
  autoRefreshRef.current = autoRefresh;

  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      if (!autoRefreshRef.current) return;
      void runFetchRef.current({ silent: true });
    }, WORKFLOW_LOG_AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh]);

  const blocks = payload?.blocks ?? [];
  const fullJson = payload ? JSON.stringify(payload, null, 2) : '';

  const codePre = isLight
    ? 'border-slate-200 bg-slate-50 text-slate-800'
    : 'border-slate-700/80 bg-slate-950 text-slate-200';

  return (
    <div
      className={`fixed inset-0 z-[110] flex justify-center px-4 pb-6 pt-10 backdrop-blur-sm ${
        isLight ? 'bg-slate-900/35' : 'bg-slate-950/80'
      }`}
    >
      <div
        className={`flex w-full max-w-3xl flex-col overflow-hidden rounded-xl border shadow-xl ${
          isLight
            ? 'border-slate-200 bg-white shadow-slate-900/10 ring-1 ring-slate-900/[0.04]'
            : 'border-slate-700/90 bg-slate-900 shadow-black/40'
        }`}
        role="dialog"
        aria-modal="true"
      >
        <header
          className={`shrink-0 border-b px-5 py-4 sm:px-6 ${
            isLight ? 'border-slate-200 bg-slate-50' : 'border-slate-800 bg-slate-900'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Node detail</p>
              <h2
                className={`mt-0.5 truncate text-lg font-semibold tracking-tight ${
                  isLight ? 'text-slate-900' : 'text-slate-50'
                }`}
              >
                {nodeName}
              </h2>
              <p className={`mt-1 text-xs ${isLight ? 'text-slate-600' : 'text-slate-500'}`}>{nodeTime}</p>
              <span className={`mt-3 inline-flex rounded-md border px-2 py-0.5 text-[11px] font-semibold ${statusBadge}`}>
                {status}
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition ${
                isLight
                  ? 'border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-100 hover:text-slate-800'
                  : 'border-slate-600/90 text-slate-400 hover:border-slate-500 hover:bg-slate-800 hover:text-slate-100'
              }`}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p
            className={`mt-3 truncate font-mono text-[10px] ${isLight ? 'text-slate-500' : 'text-slate-600'}`}
            title={categoryDetail.nodeId}
          >
            {categoryDetail.nodeId}
          </p>
        </header>

        <div
          className={`min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6 ${
            isLight ? 'bg-white' : 'bg-slate-900'
          }`}
        >
          {bgRefreshing && !loading ? (
            <div
              className={`rounded-lg border px-3 py-2 text-center text-[11px] font-medium ${
                isLight
                  ? 'border-slate-200 bg-slate-100 text-slate-600'
                  : 'border-slate-600/60 bg-slate-800/40 text-slate-400'
              }`}
            >
              Refreshing…
            </div>
          ) : null}

          {loading ? (
            <div className={`flex flex-col items-center justify-center gap-3 py-16 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
              <Loader2 className={`h-8 w-8 animate-spin ${isLight ? 'text-slate-400' : 'text-slate-500'}`} aria-hidden />
              <p className={`text-sm font-medium ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>Loading…</p>
            </div>
          ) : error ? (
            <div
              className={`rounded-lg border px-4 py-4 text-sm ${
                isLight
                  ? 'border-rose-200 bg-rose-50 text-rose-900'
                  : 'border-rose-900/40 bg-rose-950/25 text-rose-100'
              }`}
            >
              <div className="flex gap-2">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                <div>
                  <p className={`font-semibold ${isLight ? 'text-rose-800' : 'text-rose-200'}`}>Could not load</p>
                  <p className={isLight ? 'mt-1 text-rose-800/90' : 'mt-1 text-rose-100/90'}>{error}</p>
                  <button
                    type="button"
                    onClick={() => void runFetch({ silent: false })}
                    className={`mt-4 text-xs font-semibold hover:underline ${
                      isLight ? 'text-blue-700 hover:text-blue-800' : 'text-blue-400 hover:text-blue-300'
                    }`}
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {blocks.length === 0 ? (
                <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>No content in this response.</p>
              ) : (
                <div className="space-y-6">
                  {blocks.map((block, idx) => {
                    const kind = (block.kind ?? '').toLowerCase();
                    const key = `block-${idx}-${kind}`;
                    if (kind === 'heading' && block.title) {
                      return (
                        <h3
                          key={key}
                          className={`border-b pb-2 text-base font-semibold tracking-tight ${
                            isLight ? 'border-slate-200 text-slate-900' : 'border-slate-800 text-slate-50'
                          }`}
                        >
                          {block.title}
                        </h3>
                      );
                    }
                    if (kind === 'paragraph' && (block.text ?? '').trim()) {
                      return (
                        <p
                          key={key}
                          className={`text-sm leading-relaxed whitespace-pre-wrap ${
                            isLight ? 'text-slate-700' : 'text-slate-300'
                          }`}
                        >
                          {block.text}
                        </p>
                      );
                    }
                    if (kind === 'code' && (block.code ?? '').trim()) {
                      const formatted = prettyFormatCode(block.code, block.codeLanguage);
                      return (
                        <div key={key} className="space-y-2">
                          {block.title ? (
                            <div className={`text-xs font-semibold ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>
                              {block.title}
                            </div>
                          ) : null}
                          <pre
                            className={`max-h-[min(24rem,50vh)] overflow-auto rounded-lg border p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words ${codePre}`}
                          >
                            {formatted}
                          </pre>
                          {block.codeLanguage ? (
                            <div className={`text-[10px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>
                              {block.codeLanguage}
                            </div>
                          ) : null}
                        </div>
                      );
                    }
                    if (kind === 'table' && block.table != null) {
                      return (
                        <div key={key} className="space-y-2">
                          {block.title ? (
                            <div className={`text-xs font-semibold ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>
                              {block.title}
                            </div>
                          ) : null}
                          {renderTable(block.table, isLight)}
                        </div>
                      );
                    }
                    return (
                      <pre
                        key={key}
                        className={`overflow-x-auto rounded-lg border p-3 font-mono text-xs whitespace-pre-wrap ${
                          isLight
                            ? 'border-slate-200 bg-slate-50 text-slate-800'
                            : 'border-slate-700 bg-slate-950 text-slate-300'
                        }`}
                      >
                        {JSON.stringify(block, null, 2)}
                      </pre>
                    );
                  })}
                </div>
              )}

              {payload && fullJson ? (
                <section className={`space-y-2 border-t pt-6 ${isLight ? 'border-slate-200' : 'border-slate-800'}`}>
                  <h3 className={`text-[11px] font-semibold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>
                    Full response (JSON)
                  </h3>
                  <pre
                    className={`max-h-[40vh] overflow-auto rounded-lg border p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words ${codePre}`}
                  >
                    {fullJson}
                  </pre>
                </section>
              ) : null}
            </>
          )}
        </div>

        <footer
          className={`flex shrink-0 flex-col gap-3 border-t px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 ${
            isLight ? 'border-slate-200 bg-slate-100' : 'border-slate-800 bg-slate-950'
          }`}
        >
          <p className={`text-center text-[11px] sm:text-left ${isLight ? 'text-slate-500' : 'text-slate-600'}`}>
            Manual refresh or optional timed updates.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
            <WorkflowLogModalToolbar
              onManualRefresh={() => void runFetch({ silent: false })}
              isRefreshing={loading || bgRefreshing}
              autoRefreshEnabled={autoRefresh}
              onAutoRefreshEnabledChange={setAutoRefresh}
              disabled={!resolveRunLogsBaseUrl()}
              isLight={isLight}
            />
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
                isLight
                  ? 'bg-slate-900 text-white hover:bg-slate-800'
                  : 'bg-slate-100 text-slate-900 hover:bg-white'
              }`}
            >
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DetailedNodeLogModal;
